import React, { useState, useEffect } from "react";
import { apiFetch } from "../../api.js";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout.js";

const StaffMasterGrid = () => {
  const navigate = useNavigate();
  const [gridData, setGridData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownValue, setDropdownValue] = useState("ACTIVE");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  /*---------------Style--------------*/
  const styles = {
    pageContainer: {
      width: "100%",
      minHeight: "100vh",
      backgroundColor: "#f4f6f8",
      display: "flex",
      justifyContent: "center",
      padding: "10px",
    },
    container: {
      width: "100%",
      maxWidth: "1300px",
      padding: "20px",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    },
    titleRow: {
      textAlign: "center",
      fontSize: "1.6rem",
      fontWeight: "bold",
      marginBottom: "20px",
    },
    filterRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      justifyContent: "center",
      marginBottom: "20px",
    },
    searchInput: {
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      minWidth: "150px",
      flexGrow: 1,
      maxWidth: "200px",
    },
    select: {
      padding: "8px 10px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      minWidth: "140px",
      flexGrow: 1,
      maxWidth: "200px",
    },
    searchButton: {
      padding: "10px 14px",
      backgroundColor: "#2196F3",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
    },
    addButton: {
      padding: "10px 16px",
      backgroundColor: "#3a28a0ff",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
    },
    tableWrapper: {
      overflowX: "auto",
      width: "100%",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "900px",
    },
    th: {
      padding: "10px",
      backgroundColor: "#8B1E3F",
      color: "#fff",
      border: "1px solid #ddd",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "8px",
      border: "1px solid #ddd",
      fontSize: "0.85rem",
    },
    actionButton: (bg) => ({
      padding: "6px 10px",
      backgroundColor: bg,
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      marginRight: "5px",
    }),
    pagination: {
      marginTop: "15px",
      display: "flex",
      justifyContent: "center",
      gap: "10px",
    },
    pageBtn: {
      padding: "6px 12px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      cursor: "pointer",
    },
  };

  useEffect(() => {
    fetchGrid();
  }, [dropdownValue]);

  const fetchGrid = async () => {
    try {
      setLoading(true);
      const response = await apiFetch("/api/staff-grid", {
        method: "POST",
        body: JSON.stringify({ status: dropdownValue }),
      });
      const data = await response.json();
      const grid = data.data || [];
      setGridData(grid);
      setFilteredData(grid);
    } catch (err) {
      console.error("Grid fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      fetchGrid();
      return;
    }
    try {
      setLoading(true);
      const response = await apiFetch("/api/staff-search", {
        method: "POST",
        body: JSON.stringify({ searchText: searchValue }),
      });
      const data = await response.json();
      const result = data.data || [];
      setGridData(result);
      setFilteredData(result);
      setCurrentPage(1);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (unqid) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this staff record?"
    );
    if (!confirmDelete) return;
    try {
      setLoading(true);
      const response = await apiFetch("/api/staff-delete", {
        method: "POST",
        body: JSON.stringify({ unqid }),
      });
      const data = await response.json();
      alert(data.err || "Deleted successfully");
      fetchGrid();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting record");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div style={styles.pageContainer}>
        <div style={styles.container}>
          <div style={styles.titleRow}>Staff Master Grid</div>

          {/* FILTER BAR */}
          <div style={styles.filterRow}>
            {/* DROPDOWN */}
            <select
              value={dropdownValue}
              onChange={(e) => setDropdownValue(e.target.value)}
              style={styles.select}
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </select>

            {/* SEARCH TEXT */}
            <input
              type="text"
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={styles.searchInput}
            />

            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={styles.select}
            >
              {[10, 20, 50, 100].map((s) => (
                <option key={s} value={s}>
                  {s} / page
                </option>
              ))}
            </select>
            <button style={styles.searchButton} onClick={handleSearch}>
              Search
            </button>
            <button
              style={styles.addButton}
              onClick={() => navigate("/StaffMaster")}
            >
              Add New
            </button>
          </div>
          {/* GRID TABLE */}
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Designation</th>
                  <th style={styles.th}>Head Name</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.mcm_14}>
                      <td style={styles.td}>{row.mcm_11}</td>
                      <td style={styles.td}>{row.mcm_15}</td>
                      <td style={styles.td}>{row.mcm_35}</td>
                      <td style={styles.td}>{row.mcm_36}</td>
                      <td style={styles.td}>{row.status}</td>

                      <td style={styles.td}>
                        <button
                          style={styles.actionButton("#4CAF50")}
                          onClick={() =>
                            navigate("/StaffMaster", {
                              state: { unqid: row.mcm_14, mode: "edit" },
                            })
                          }
                        >
                          Edit
                        </button>
                        <button
                          style={styles.actionButton("#f44336")}
                          onClick={() => handleDelete(row.mcm_14)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} style={styles.td}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* PAGINATION */}
          <div style={styles.pagination}>
            <button
              style={styles.pageBtn}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>

            <span>
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              style={styles.pageBtn}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StaffMasterGrid;
